import { EagerLoaded } from "../../types/base/orm";
import { DocumentCategory } from "../../types/documentCategory";
import {
    FinancialDocument,
    FinancialDocumentBase,
    FinancialDocumentCounterparty,
    FinancialDocumentExtended,
    FinancialDocumentLinkedItems,
    LinkedBill,
    LinkedInvoice,
    LinkedJournalEntry,
} from "../../types/financialDocument";
import { EntityWithUser } from "../../types/entity";
import { EntityDto, EntityWithUserDto } from "../entity/entity.dto";
import { TransactionMatchDto } from "../transactionMatch/transaction-match.dto";
import { TransactionMatch } from "../../types/transactionMatch";
import { FinancialAccountDto } from "../financialAccount/financial-account.dto";
import { IntegrationAccountDto } from "../financialAccount/integration-account.dto";
import { UserDto } from "../user/user.dto";
import { AccountInfoExtraction } from "../../schemas/financialDocument/account-info-extraction.schema";
import { BalanceSheetExtractionRecord } from "../../schemas/financialDocument/balance-sheet-extraction-record.schema";
import { CoaExtractionRecord } from "../../schemas/financialDocument/coa-extraction-record.schema";
import { GlExtractionRecord } from "../../schemas/financialDocument/gl-extraction-record.schema";
import { DocumentInvoicesExtractionRecord } from "../../schemas/financialDocument/document-invoices-extraction-record.schema";
import { DocumentBillsExtractionRecord } from "../../schemas/financialDocument/document-bills-extraction-record.schema";
import {
    billExtractionsFromPersisted,
    invoiceExtractionsFromPersisted,
    PersistedBillExtraction,
    PersistedBillExtractionLineItem,
    PersistedInvoiceExtraction,
    PersistedInvoiceExtractionLineItem,
} from "../../helpers/invoice-extractions-from-persisted.helper";
import { InvoiceExtractionLineItem } from "../../schemas/financialDocument/invoice-extraction-line-item.schema";
import { ExtractionRowCounts } from "../../schemas/financialDocument/extraction-row-counts.schema";
import { FinancialDocumentDto } from "./financial-document.dto";

/**
 * Input type for FinancialDocumentExtendedDto constructor.
 *
 * Omits computed DTO fields from FinancialDocumentExtended
 * and adds `bankReconciliationDocuments` from FinancialDocumentBase (used to compute isReconciled).
 *
 * The cast from TypeORM Entity to this type must happen at the service/controller
 * layer because common/dto cannot import entities from backendCommon/entities.
 *
 * Callers should use assertRelationLoaded() before casting to ensure
 * required relations are loaded.
 */
export type FinancialDocumentExtendedDtoInput = Omit<
    FinancialDocumentExtended,
    | "isReconciled"
    | "cashAppExcludedLinkedAccountPaymentCount"
    | "accrualReviewStatus"
    | "linkedJournalEntries"
    | "linkedInvoices"
    | "linkedBills"
> &
    Pick<Required<FinancialDocumentBase>, "bankReconciliationDocuments">;

/**
 * Extended DTO for FinancialDocument - with REQUIRED relations.
 * Used by all financial-document module endpoints where relations are always loaded.
 * Relations are required (not optional) because the backend always loads them for these endpoints.
 */
export class FinancialDocumentExtendedDto extends FinancialDocumentDto {
    static deserialize(
        dto: FinancialDocumentExtendedDto,
    ): FinancialDocumentExtended {
        const base: FinancialDocument = FinancialDocumentDto.deserialize(dto);

        return {
            ...base,
            entity: dto.entity
                ? (EntityDto.deserialize(dto.entity) as EntityWithUser)
                : null,
            financialAccount: dto.financialAccount
                ? FinancialAccountDto.deserialize(dto.financialAccount)
                : null,
            transactionMatches: (dto.transactionMatches ?? []).map(
                TransactionMatchDto.deserialize,
            ) as Array<EagerLoaded<TransactionMatch, "financialTransaction">>,
            integrationAccount: dto.integrationAccount
                ? IntegrationAccountDto.deserialize(dto.integrationAccount)
                : null,
            uploader: dto.uploader ? UserDto.deserialize(dto.uploader) : null,
            documentCategory: dto.documentCategory ?? null,
            counterparty: dto.counterparty ?? null,
            accountInfoExtraction: dto.accountInfoExtraction ?? null,
            balanceSheetExtraction: dto.balanceSheetExtraction ?? null,
            coaExtraction: dto.coaExtraction ?? null,
            glExtraction: dto.glExtraction ?? null,
            documentInvoicesExtraction: dto.documentInvoicesExtraction ?? null,
            documentBillsExtraction: dto.documentBillsExtraction ?? null,
            linkedJournalEntries: dto.linkedJournalEntries ?? [],
            linkedInvoices: dto.linkedInvoices ?? [],
            linkedBills: dto.linkedBills ?? [],
        };
    }

    entity: EntityDto | null;
    financialAccount: FinancialAccountDto | null;
    transactionMatches: TransactionMatchDto[];
    integrationAccount: IntegrationAccountDto | null;
    uploader: UserDto | null;
    documentCategory: DocumentCategory | null;
    counterparty: FinancialDocumentCounterparty | null;
    accountInfoExtraction: AccountInfoExtraction | null;
    balanceSheetExtraction: BalanceSheetExtractionRecord | null;
    coaExtraction: CoaExtractionRecord | null;
    glExtraction: GlExtractionRecord | null;
    documentInvoicesExtraction: DocumentInvoicesExtractionRecord | null;
    documentBillsExtraction: DocumentBillsExtractionRecord | null;
    linkedJournalEntries: LinkedJournalEntry[];
    linkedInvoices: LinkedInvoice[];
    linkedBills: LinkedBill[];

    constructor(
        financialDocument: FinancialDocumentExtendedDtoInput,
        extractionRowCounts: ExtractionRowCounts,
        linkedItems: FinancialDocumentLinkedItems,
    ) {
        super(financialDocument);
        const entityValue = financialDocument.entity;
        this.entity = entityValue ? new EntityWithUserDto(entityValue) : null;

        this.financialAccount = financialDocument.financialAccount
            ? new FinancialAccountDto(financialDocument.financialAccount)
            : null;

        this.transactionMatches = financialDocument.transactionMatches.map(
            (tm) => new TransactionMatchDto(tm),
        );

        this.integrationAccount = financialDocument.integrationAccount
            ? new IntegrationAccountDto(financialDocument.integrationAccount)
            : null;

        this.uploader = financialDocument.uploader
            ? new UserDto(financialDocument.uploader)
            : null;

        this.documentCategory = financialDocument.documentCategory ?? null;

        this.counterparty = financialDocument.counterparty
            ? {
                  id: financialDocument.counterparty.id,
                  name: financialDocument.counterparty.name,
              }
            : null;

        this.accountInfoExtraction =
            financialDocument.accountInfoExtraction ?? null;

        const balanceSheetExtraction = financialDocument.balanceSheetExtraction;
        this.balanceSheetExtraction = balanceSheetExtraction
            ? {
                  status: balanceSheetExtraction.status,
                  rowsCount: extractionRowCounts.balanceSheet,
                  ...(balanceSheetExtraction.rows != null
                      ? { rows: balanceSheetExtraction.rows }
                      : {}),
                  date: balanceSheetExtraction.date ?? null,
                  warnings: balanceSheetExtraction.warnings ?? null,
                  originalExtraction: balanceSheetExtraction.originalExtraction,
                  isChangedByUser: balanceSheetExtraction.isChangedByUser,
              }
            : null;

        const coaExtraction = financialDocument.coaExtraction;
        this.coaExtraction = coaExtraction
            ? {
                  status: coaExtraction.status,
                  extractionMethod: coaExtraction.extractionMethod,
                  rowsCount: extractionRowCounts.coa,
                  ...(coaExtraction.rows != null
                      ? { rows: coaExtraction.rows }
                      : {}),
                  warnings: coaExtraction.warnings ?? null,
                  rejection: coaExtraction.rejection ?? null,
                  originalExtraction: coaExtraction.originalExtraction,
                  isChangedByUser: coaExtraction.isChangedByUser,
              }
            : null;

        const glExtraction = financialDocument.glExtraction;
        this.glExtraction = glExtraction
            ? {
                  status: glExtraction.status,
                  extractionMethod: glExtraction.extractionMethod,
                  basis: glExtraction.basis ?? null,
                  rowsCount: extractionRowCounts.gl,
                  ...(glExtraction.rows != null
                      ? { rows: glExtraction.rows }
                      : {}),
                  startDate: glExtraction.startDate ?? null,
                  endDate: glExtraction.endDate ?? null,
                  warnings: glExtraction.warnings ?? null,
                  rejection: glExtraction.rejection ?? null,
                  originalExtraction: glExtraction.originalExtraction,
                  isChangedByUser: glExtraction.isChangedByUser,
              }
            : null;

        this.documentInvoicesExtraction = toDocumentInvoicesExtractionRecord(
            financialDocument.documentInvoicesExtraction,
        );
        this.documentBillsExtraction = toDocumentBillsExtractionRecord(
            financialDocument.documentBillsExtraction,
        );

        this.linkedJournalEntries = linkedItems.linkedJournalEntries;
        this.linkedInvoices = linkedItems.linkedInvoices;
        this.linkedBills = linkedItems.linkedBills;
    }
}

interface DocumentInvoicesExtractionInput {
    status: DocumentInvoicesExtractionRecord["status"];
    isChangedByUser: boolean;
    originalExtraction: DocumentInvoicesExtractionRecord["originalExtraction"];
    invoiceExtractions?: DocumentInvoicesExtractionRecord["invoiceExtractions"];
}

interface DocumentBillsExtractionInput {
    status: DocumentBillsExtractionRecord["status"];
    isChangedByUser: boolean;
    originalExtraction: DocumentBillsExtractionRecord["originalExtraction"];
    billExtractions?: DocumentBillsExtractionRecord["billExtractions"];
}

function isPersistedInvoiceExtractionLineItem(
    lineItem: InvoiceExtractionLineItem | PersistedInvoiceExtractionLineItem,
): lineItem is PersistedInvoiceExtractionLineItem {
    return "id" in lineItem;
}

function isPersistedBillExtractionLineItem(
    lineItem: InvoiceExtractionLineItem | PersistedBillExtractionLineItem,
): lineItem is PersistedBillExtractionLineItem {
    return "id" in lineItem;
}

function toPersistedInvoiceExtractionLineItem(
    lineItem: InvoiceExtractionLineItem | PersistedInvoiceExtractionLineItem,
    rowIndex: number,
): PersistedInvoiceExtractionLineItem {
    if (isPersistedInvoiceExtractionLineItem(lineItem)) {
        return {
            id: lineItem.id,
            rowIndex,
            item: lineItem.item ?? null,
            description: lineItem.description,
            amount: lineItem.amount,
            quantity: lineItem.quantity ?? null,
            unitPrice: lineItem.unitPrice ?? null,
            classIds: lineItem.classIds,
            accountId: lineItem.accountId,
            accrualAccountId: lineItem.accrualAccountId,
            accountIdSource: lineItem.accountIdSource,
            catalogItemId: lineItem.catalogItemId ?? null,
            catalogItemPriceId: lineItem.catalogItemPriceId ?? null,
        };
    }

    return {
        id: lineItem.lineId,
        rowIndex,
        item: lineItem.item ?? null,
        description: lineItem.description,
        amount: lineItem.amount,
        quantity: lineItem.quantity ?? null,
        unitPrice: lineItem.unitPrice ?? null,
        classIds: lineItem.classIds,
        accountId: lineItem.accountId,
        accrualAccountId: lineItem.accrualAccountId,
        accountIdSource: lineItem.accountIdSource ?? null,
        catalogItemId: lineItem.catalogItemId ?? null,
        catalogItemPriceId: lineItem.catalogItemPriceId ?? null,
    };
}

function toPersistedBillExtractionLineItem(
    lineItem: InvoiceExtractionLineItem | PersistedBillExtractionLineItem,
    rowIndex: number,
): PersistedBillExtractionLineItem {
    if (isPersistedBillExtractionLineItem(lineItem)) {
        return {
            id: lineItem.id,
            rowIndex,
            item: lineItem.item ?? null,
            description: lineItem.description,
            amount: lineItem.amount,
            quantity: lineItem.quantity ?? null,
            unitPrice: lineItem.unitPrice ?? null,
            classIds: lineItem.classIds,
            accountId: lineItem.accountId,
            accrualAccountId: lineItem.accrualAccountId,
            accountIdSource: lineItem.accountIdSource,
        };
    }

    return {
        id: lineItem.lineId,
        rowIndex,
        item: lineItem.item ?? null,
        description: lineItem.description,
        amount: lineItem.amount,
        quantity: lineItem.quantity ?? null,
        unitPrice: lineItem.unitPrice ?? null,
        classIds: lineItem.classIds,
        accountId: lineItem.accountId,
        accrualAccountId: lineItem.accrualAccountId,
        accountIdSource: lineItem.accountIdSource ?? null,
    };
}

function toPersistedInvoiceExtractions(
    invoiceExtractions: NonNullable<
        DocumentInvoicesExtractionInput["invoiceExtractions"]
    >,
): PersistedInvoiceExtraction[] {
    return invoiceExtractions.map((extraction, orderIndex) => ({
        id: extraction.id,
        orderIndex,
        counterpartyName: extraction.counterpartyName,
        counterpartyId: extraction.counterpartyId,
        counterpartyIdSource: extraction.counterpartyIdSource ?? null,
        documentNumber: extraction.documentNumber,
        documentDate: extraction.documentDate,
        dueDate: extraction.dueDate,
        totalAmount: extraction.totalAmount,
        taxAmount: extraction.taxAmount ?? null,
        discountAmount: extraction.discountAmount ?? null,
        depositAmount: extraction.depositAmount ?? null,
        memo: extraction.memo ?? null,
        balanceDue: extraction.balanceDue ?? null,
        paymentDate: extraction.paymentDate ?? null,
        entityId: extraction.entityId,
        warnings: extraction.warnings,
        lineItems: (extraction.lineItems ?? []).map((lineItem, rowIndex) =>
            toPersistedInvoiceExtractionLineItem(lineItem, rowIndex),
        ),
    }));
}

function toPersistedBillExtractions(
    billExtractions: NonNullable<
        DocumentBillsExtractionInput["billExtractions"]
    >,
): PersistedBillExtraction[] {
    return billExtractions.map((extraction, orderIndex) => ({
        id: extraction.id,
        orderIndex,
        counterpartyName: extraction.counterpartyName,
        counterpartyId: extraction.counterpartyId,
        counterpartyIdSource: extraction.counterpartyIdSource ?? null,
        documentNumber: extraction.documentNumber,
        documentDate: extraction.documentDate,
        dueDate: extraction.dueDate,
        totalAmount: extraction.totalAmount,
        taxAmount: extraction.taxAmount ?? null,
        discountAmount: extraction.discountAmount ?? null,
        depositAmount: extraction.depositAmount ?? null,
        memo: extraction.memo ?? null,
        balanceDue: extraction.balanceDue ?? null,
        paymentDate: extraction.paymentDate ?? null,
        entityId: extraction.entityId,
        warnings: extraction.warnings,
        lineItems: (extraction.lineItems ?? []).map((lineItem, rowIndex) =>
            toPersistedBillExtractionLineItem(lineItem, rowIndex),
        ),
    }));
}

function mapPersistedInvoiceExtractions(
    invoiceExtractions: DocumentInvoicesExtractionInput["invoiceExtractions"],
): DocumentInvoicesExtractionRecord["invoiceExtractions"] {
    if (!invoiceExtractions?.length) {
        return [];
    }

    return invoiceExtractionsFromPersisted({
        invoiceExtractions: toPersistedInvoiceExtractions(invoiceExtractions),
    });
}

function mapPersistedBillExtractions(
    billExtractions: DocumentBillsExtractionInput["billExtractions"],
): DocumentBillsExtractionRecord["billExtractions"] {
    if (!billExtractions?.length) {
        return [];
    }

    return billExtractionsFromPersisted({
        billExtractions: toPersistedBillExtractions(billExtractions),
    });
}

function toDocumentInvoicesExtractionRecord(
    extraction: DocumentInvoicesExtractionInput | null | undefined,
): DocumentInvoicesExtractionRecord | null {
    if (!extraction) {
        return null;
    }

    return {
        status: extraction.status,
        isChangedByUser: extraction.isChangedByUser,
        originalExtraction: extraction.originalExtraction,
        invoiceExtractions: mapPersistedInvoiceExtractions(
            extraction.invoiceExtractions,
        ),
    };
}

function toDocumentBillsExtractionRecord(
    extraction: DocumentBillsExtractionInput | null | undefined,
): DocumentBillsExtractionRecord | null {
    if (!extraction) {
        return null;
    }

    return {
        status: extraction.status,
        isChangedByUser: extraction.isChangedByUser,
        originalExtraction: extraction.originalExtraction,
        billExtractions: mapPersistedBillExtractions(
            extraction.billExtractions,
        ),
    };
}
